import type { EvaluationOutcome, NotEvaluableReason } from "@/types";

/**
 * Turns whatever the server sent into the four-state union the screens render.
 *
 * Two rules, both from C5 (TECH_SPEC_FE §1):
 *
 * 1. **A missing or unrecognised state is never `failed`.** `failed` means an
 *    execution did not reach an answer — something to go fix. Guessing it from
 *    a field the server did not send puts work in front of an operator that
 *    does not exist.
 * 2. **A branch without a score carries no score.** The union makes that a type
 *    error rather than a convention, so "평가 불가 renders as 0" cannot compile.
 *
 * BE reads these from the latest `qa_replay_run` (BE §19.3.1): no run is
 * `not_run`, a run still waiting for the scorer is `not_evaluable` /
 * `not_scored_yet`. A score keeps which rubric and policy it came under, so a
 * screen can keep a v1 row out of a v2 mean (utils/rubric). Other keys
 * (`qa_replay_run_id`, `run_at`, …) are ignored here; screens that need them
 * read the raw row.
 */
export function evaluationOutcomeFrom(raw: unknown): EvaluationOutcome {
  if (!raw || typeof raw !== "object") return { status: "not_run" };
  const value = raw as Record<string, unknown>;

  switch (value.status) {
    case "scored": {
      const score = typeof value.score === "number" ? value.score : null;
      // A "scored" row with no number is not scored, whatever it calls itself.
      if (score === null) return { status: "not_evaluable", reason: "not_scored_yet" };
      return {
        status: "scored",
        score,
        reference_snapshot_id:
          typeof value.reference_snapshot_id === "string" ? value.reference_snapshot_id : null,
        rubric_version: typeof value.rubric_version === "string" ? value.rubric_version : null,
        evaluation_policy:
          typeof value.evaluation_policy === "string" ? value.evaluation_policy : null,
        answer_quality: typeof value.answer_quality === "number" ? value.answer_quality : null,
        execution: typeof value.execution === "number" ? value.execution : null,
      };
    }
    case "not_evaluable":
      return { status: "not_evaluable", reason: notEvaluableReason(value.reason) };
    case "failed":
      return { status: "failed", reason: typeof value.reason === "string" ? value.reason : null };
    default:
      return { status: "not_run" };
  }
}

function notEvaluableReason(raw: unknown): NotEvaluableReason {
  return typeof raw === "string" && raw ? raw : "not_scored_yet";
}

/** What the reason says to someone who is not holding the spec. */
export function notEvaluableLabel(reason: NotEvaluableReason): string {
  switch (reason) {
    case "no_reference":
      return "검토에서 통과한 Reference Answer 가 없습니다";
    case "reference_stale":
      return "Reference 가 현재 기준보다 오래됐습니다";
    case "criteria_changed":
      return "기준이 변경되어 재실행이 필요합니다";
    case "judge_failed":
    case "judge_truncated":
      return "실행은 완료됐지만 채점에 실패했습니다";
    case "criterion_unjudgeable":
      return "확정 기준을 판정할 자료가 부족합니다";
    case "missing_criteria":
      return "채점 기준이 없습니다";
    case "empty_answer":
      return "채점할 답변이 없습니다";
    case "invalid_criteria":
    case "legacy_criteria":
      return "채점 기준 보완이 필요합니다";
    case "criteria_needs_author":
      return "채점 기준을 사람이 작성해야 합니다";
    case "criteria_generation_failed":
      return "채점 기준 생성에 실패했습니다";
    case "criteria_not_answer_quality":
      return "답변 품질 측정 대상이 아닙니다 (AI 개입 판단 문제)";
    case "not_scored_yet":
      return "실행은 끝났고 아직 채점되지 않았습니다";
    default:
      return reason;
  }
}

/**
 * Counts for a summary row. Kept beside the normaliser so the four states are
 * enumerated in one place — a fifth one would break this and be noticed.
 */
export function countOutcomes(outcomes: readonly EvaluationOutcome[]) {
  const counts = { not_run: 0, scored: 0, not_evaluable: 0, failed: 0 };
  for (const outcome of outcomes) counts[outcome.status] += 1;
  return counts;
}
